/**
 * Snowplow suppression — the MECHANISM lives here, the LIST is app policy.
 *
 * Destination routing has to be decided at the pipeline, because that is the
 * only place every emit passes through. It used to be decided inside one of
 * two app facades (`trackV2`), so which entry point a call site imported
 * decided whether the policy applied at all — and when that was measured, 16
 * call sites across 15 files reached the pipeline through `track()` and
 * bypassed it, including the toast-layer `error_viewed` auto-fire the denylist
 * was written for, against 4 that honoured it.
 *
 * The app registers its denylist from `src/lib/analytics/wiring.ts` beside the
 * other `register*` calls. The package holds no event names of its own — an
 * event name is warehouse policy, not runtime behaviour.
 *
 * THE DEFAULT IS EMPTY, AND THAT IS LOAD-BEARING. An unwired pipeline (the
 * package's own tests, Storybook, any non-app entry point) suppresses nothing
 * and routes exactly as it did before this registry existed — which is what
 * keeps `track.parity.test.ts` passing against an unchanged `parity/expected.ts`
 * while the app-side harness, which imports the real wiring, keeps deriving a
 * null Snowplow leg for the denylisted fixture.
 *
 * PRE-WIRING SEMANTICS: THE LIST IS READ AT ENRICH TIME, AND AN EVENT TRACKED
 * BEFORE REGISTRATION KEEPS ITS SNOWPLOW LEG. `enrich()` computes the route set
 * when the event is tracked; `dispatchEvent` may then hold that event in the
 * pre-wiring buffer and flush it later, but the route it flushes is the one
 * already decided. So an event tracked before the denylist registers reaches
 * Snowplow even if registration lands before the flush.
 *
 * That is ACCEPTED rather than fixed, for three reasons. It matches how every
 * other routing and enrichment input already behaves — `options`, the context
 * providers, the Chronicle snapshot are all read at enrich time — so consulting
 * this one list at dispatch time instead would put two timing models inside one
 * pipeline for no gain in the general case. It would not even repair the edge it
 * targets: the same pre-wiring event enriches from an empty context registry, so
 * withholding it from Snowplow would trade a correctly-labelled event for a
 * suppressed one and leave the impoverished props on the four legs that did
 * fire. And no production path reaches it — app wiring registers at module
 * scope, which completes before any React render or effect can call `track()`.
 * The paths that DO reach it are the non-app entry points, where empty is the
 * behaviour we want. The read-at-enrich-time ordering is pinned by a test in
 * `./suppression.test.ts` rather than left to be inferred from this paragraph.
 */

let suppressedEvents: ReadonlySet<string> = new Set();

/**
 * Register the app's Snowplow denylist. REPLACES the previous set rather than
 * merging, so the registry always reflects exactly one policy and a re-run of
 * app wiring (HMR, a test re-importing it) cannot accumulate names.
 */
export function registerSnowplowSuppressedEvents(names: readonly string[]): void {
  suppressedEvents = new Set(names);
}

/**
 * Whether this event's Snowplow send is suppressed. Read by `enrich()` — the
 * only reader of `options.snowplow` between the facades and the destinations.
 */
export function isSnowplowSuppressed(eventName: string): boolean {
  return suppressedEvents.has(eventName);
}

/** Test-only: drop back to the empty (unwired) default between tests. */
export function __resetSnowplowSuppressionForTests(): void {
  suppressedEvents = new Set();
}
